import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  SystemEntity,
  EntityAssociationType,
  MatchConfidenceStatus,
} from "@erp/shared-types";
import { SystemEntitySchema } from "@erp/workflows";

const UUIDSchema = z.uuid();

/**
 * Input schema for listing entity associations
 */
export const ListEntityAssociationsInputSchema = z.object({
  entityId: UUIDSchema,
  entityType: z.enum(SystemEntity),
  associationType: z.enum(EntityAssociationType).optional(),
});

/**
 * Metadata schema for match associations (confidence tracking)
 */
export const EntityAssociationMatchMetadataSchema = z
  .object({
    confidence: z.number().optional(),
    matchStatus: z.enum(MatchConfidenceStatus).optional(),
    matchStatusReason: z.string().optional(),
    confirmedBy: UUIDSchema.nullable().optional(),
    confirmedAt: z.string().nullable().optional(),
    /**
     * Set when several AP invoice lines were rolled up to match a single
     * PO/SO/TO line or charge. All member associations share the same
     * `aggregationGroupId`. Set by the aggregation retry pass in
     * `InvoiceReconciliationService.retryUnmatchedLinesWithAggregation`.
     */
    aggregationGroupId: z.string().nullable().optional(),
    aggregatedMemberCount: z.number().nullable().optional(),
    aggregatedTargetKind: z
      .enum(["PO_LINE", "SO_LINE", "TO_LINE", "CHARGE"])
      .nullable()
      .optional(),
    aggregatedReasoning: z.string().nullable().optional(),
    /**
     * Short canonical description the agent proposed for the group (e.g.
     * "Mango"). Rendered on the aggregation subtotal row.
     */
    aggregatedCommonDescription: z.string().nullable().optional(),
  })
  .passthrough()
  .nullable()
  .optional();

/**
 * Output schema for entity associations
 * Returns the associated entities (not the queried entity) with their association types
 */
export const EntityAssociationItemSchema = z.object({
  entity: SystemEntitySchema,
  associationType: z.enum(EntityAssociationType).nullable().optional(),
  metadata: EntityAssociationMatchMetadataSchema,
});

export const ListEntityAssociationsResponseSchema = z.object({
  associations: z.array(EntityAssociationItemSchema),
});

/**
 * Input schema for creating entity association
 */
export const CreateEntityAssociationInputSchema = z.object({
  sourceEntityType: z.enum(SystemEntity),
  sourceEntityId: UUIDSchema,
  targetEntityType: z.enum(SystemEntity),
  targetEntityId: UUIDSchema,
  associationType: z.enum(EntityAssociationType).optional(),
});

/**
 * Output schema for created entity association
 */
export const EntityAssociationSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  entityIdSource: UUIDSchema,
  entityTypeSource: z.enum(SystemEntity),
  entityIdTarget: UUIDSchema,
  entityTypeTarget: z.enum(SystemEntity),
  associationType: z.enum(EntityAssociationType).nullable(),
  createdBy: UUIDSchema.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * Input schema for confirming a tentative match (sets matchStatus to confirmed)
 */
export const ConfirmMatchInputSchema = z.object({
  sourceEntityType: z.enum(SystemEntity),
  sourceEntityId: UUIDSchema,
  targetEntityType: z.enum(SystemEntity),
  targetEntityId: UUIDSchema,
  associationType: z.enum(EntityAssociationType),
});

export const ConfirmMatchResponseSchema = z.object({
  success: z.boolean(),
});

/**
 * Input schema for deleting entity association
 */
export const DeleteEntityAssociationInputSchema = z.object({
  sourceEntityType: z.enum(SystemEntity),
  sourceEntityId: UUIDSchema,
  targetEntityType: z.enum(SystemEntity),
  targetEntityId: UUIDSchema,
  associationType: z.enum(EntityAssociationType).optional(),
});

/**
 * Output schema for delete operation
 */
export const DeleteEntityAssociationResponseSchema = z.object({
  success: z.boolean(),
});

export const entityAssociationsContracts = {
  list: oc
    .input(ListEntityAssociationsInputSchema)
    .output(ListEntityAssociationsResponseSchema)
    .route({
      method: "GET",
    }),
  create: oc
    .input(CreateEntityAssociationInputSchema)
    .output(EntityAssociationSchema)
    .route({
      method: "POST",
    }),
  confirmMatch: oc
    .input(ConfirmMatchInputSchema)
    .output(ConfirmMatchResponseSchema)
    .route({
      method: "POST",
    }),
  delete: oc
    .input(DeleteEntityAssociationInputSchema)
    .output(DeleteEntityAssociationResponseSchema)
    .route({
      method: "DELETE",
    }),
} as const;

export type ListEntityAssociationsInput = z.infer<
  typeof ListEntityAssociationsInputSchema
>;
export type EntityAssociationItem = z.infer<typeof EntityAssociationItemSchema>;
export type ListEntityAssociationsResponse = z.infer<
  typeof ListEntityAssociationsResponseSchema
>;
export type CreateEntityAssociationInput = z.infer<
  typeof CreateEntityAssociationInputSchema
>;
export type EntityAssociation = z.infer<typeof EntityAssociationSchema>;
export type DeleteEntityAssociationInput = z.infer<
  typeof DeleteEntityAssociationInputSchema
>;
export type DeleteEntityAssociationResponse = z.infer<
  typeof DeleteEntityAssociationResponseSchema
>;
export type ConfirmMatchInput = z.infer<typeof ConfirmMatchInputSchema>;
export type ConfirmMatchResponse = z.infer<typeof ConfirmMatchResponseSchema>;
export type EntityAssociationMatchMetadata = z.infer<
  typeof EntityAssociationMatchMetadataSchema
>;
